import { jsx, jsxs } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useId } from '@radix-ui/react-id';
import classNames from 'classnames';
import { forwardRef, useMemo, useState, isValidElement, useCallback, useEffect } from 'react';
import '../../../../utils/styles/flex.mjs';
import Stack from '../../../../utils/styles/stack.mjs';
import useInputValue from '../../hooks/useInputValue.mjs';
import { sharedStyledInputRoot, sharedStyledRoot, sharedStyledContent, sharedStylePlaceholder, sharedStyledInputPlaceholderRoot, sharedStyledLabelRoot } from '../sharedStyled.vanilla.mjs';
import { childrenRoot, styledInputRoot, styledInput, styledTextFieldPlaceholder, styledInputPlaceholderRoot, styledLabelRoot } from './baseTextField.vanilla.mjs';
import { TEXT_FIELD_LEFT_CONTENT_HORIZONTAL_SPACING } from './constants.mjs';
import { baseInput, leftInputContentRoot, leftSlotRoot, rightSlotRoot, baseInputRoot } from './textField.vanilla.mjs';

const BaseTextFieldImpl = forwardRef(
  ({
    css,
    size = "default",
    variant = "solid",
    state = "default",
    label,
    labelledBy,
    describeBy,
    leftSlot,
    rightSlot,
    required,
    children,
    disabled,
    className,
    spellCheck = false,
    ...props
  }, forwardedRef) => {
    const inputId = useId(props?.id);
    const leftInputContent = useMemo(() => {
      const inputNode = /* @__PURE__ */ jsx(
        "input",
        {
          ...props,
          className: baseInput({ size }),
          "aria-describedby": describeBy,
          "aria-labelledby": labelledBy,
          "aria-invalid": state === "error" ? true : false,
          required,
          id: inputId,
          ref: forwardedRef,
          disabled,
          spellCheck
        }
      );
      return /* @__PURE__ */ jsxs(
        Stack.Horizontal,
        {
          className: leftInputContentRoot,
          space: TEXT_FIELD_LEFT_CONTENT_HORIZONTAL_SPACING[size],
          children: [
            leftSlot && /* @__PURE__ */ jsx("div", { className: leftSlotRoot({ size, state }), children: leftSlot }),
            children && /* @__PURE__ */ jsx("div", { className: childrenRoot, children }),
            inputNode
          ]
        }
      );
    }, [
      children,
      describeBy,
      disabled,
      forwardedRef,
      inputId,
      labelledBy,
      leftSlot,
      props,
      required,
      size,
      state,
      spellCheck
    ]);
    if (isInputSizeLabelInsideType(size)) {
      return /* @__PURE__ */ jsx(
        LabelInsideTextField,
        {
          ...props,
          disabled,
          css,
          className,
          "aria-describedby": describeBy,
          "aria-labelledby": labelledBy,
          required,
          rightSlot,
          size,
          variant,
          ref: forwardedRef,
          label,
          id: inputId,
          state,
          leftSlot,
          spellCheck,
          children
        }
      );
    }
    return /* @__PURE__ */ jsxs(
      StyledLabel,
      {
        css,
        className: classNames(sharedStyledRoot, className),
        children: [
          label,
          /* @__PURE__ */ jsxs(
            "div",
            {
              ...props.readOnly && { ["data-read-only"]: "" },
              className: classNames(
                sharedStyledInputRoot({ disabled, state, variant }),
                baseInputRoot({ size }),
                styledInputRoot
              ),
              children: [
                leftInputContent,
                /* @__PURE__ */ jsx("div", { className: rightSlotRoot, children: rightSlot })
              ]
            }
          )
        ]
      }
    );
  }
);
function isInputSizeLabelInsideType(size) {
  return size === "large" || size === "xlarge";
}
const LabelInsideTextFieldImpl = forwardRef(
  ({
    css,
    label,
    leftSlot,
    rightSlot,
    size,
    value: valueFromProps,
    defaultValue,
    variant,
    state,
    disabled,
    placeholder,
    onChange: onChangeFromProps,
    children,
    className,
    autoFocus,
    ...props
  }, forwardedRef) => {
    const [inputActive, setInputActive] = useState(false);
    const hasChildren = isValidElement(children);
    const [value, onChange] = useInputValue({
      value: valueFromProps,
      defaultValue,
      onChange: onChangeFromProps
    });
    const isValueEmpty = isNil(value) || value === "";
    const hasLabelElement = !isNil(label);
    const handleInputActive = useCallback(() => {
      setInputActive(true);
    }, []);
    useEffect(() => {
      if (!isNil(value) && value !== "") {
        handleInputActive();
      }
    }, [handleInputActive, value]);
    useEffect(() => {
      if (autoFocus) {
        handleInputActive();
      }
    }, [autoFocus, handleInputActive]);
    const inputContent = /* @__PURE__ */ jsxs("div", { className: sharedStyledContent, children: [
      /* @__PURE__ */ jsx("div", { className: childrenRoot, children }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: classNames(
            styledInputPlaceholderRoot({ hasLeftContent: hasChildren }),
            sharedStyledInputPlaceholderRoot
          ),
          children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                ...props,
                className: classNames(
                  baseInput({ size }),
                  styledInput({ hasChildren, hasLabel: hasLabelElement, size })
                ),
                autoFocus,
                disabled,
                value,
                ref: forwardedRef,
                placeholder,
                onChange,
                onFocus: composeEventHandlers(props.onFocus, () => {
                  if (!props.readOnly) {
                    handleInputActive();
                  }
                }),
                onBlur: composeEventHandlers(
                  props.onBlur,
                  () => isValueEmpty && setInputActive(false)
                )
              }
            ),
            /* @__PURE__ */ jsx(
              "p",
              {
                className: classNames(
                  styledTextFieldPlaceholder,
                  sharedStylePlaceholder({
                    inactive: !inputActive,
                    required: props.required
                  })
                ),
                children: placeholder
              }
            )
          ]
        }
      ),
      hasChildren ? null : /* @__PURE__ */ jsx(
        "div",
        {
          className: classNames(
            sharedStyledLabelRoot,
            styledLabelRoot({ inactive: !inputActive, size })
          ),
          children: label
        }
      )
    ] });
    return /* @__PURE__ */ jsx(
      StyledLabel,
      {
        css,
        className: classNames(sharedStyledRoot, className),
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: classNames(
              sharedStyledInputRoot({ disabled, state, variant }),
              baseInputRoot({ size }),
              styledInputRoot
            ),
            ...props.readOnly && { ["data-read-only"]: "" },
            children: [
              /* @__PURE__ */ jsxs(
                Stack.Horizontal,
                {
                  space: TEXT_FIELD_LEFT_CONTENT_HORIZONTAL_SPACING[size],
                  className: leftInputContentRoot,
                  children: [
                    leftSlot && /* @__PURE__ */ jsx("div", { className: leftSlotRoot({ size, state }), children: leftSlot }),
                    inputContent
                  ]
                }
              ),
              /* @__PURE__ */ jsx("div", { className: rightSlotRoot, children: rightSlot })
            ]
          }
        )
      }
    );
  }
);
const StyledLabel = styled("label");
const LabelInsideTextField = styled(LabelInsideTextFieldImpl);
const BaseTextField = styled(BaseTextFieldImpl);

export { BaseTextField };
//# sourceMappingURL=BaseTextField.mjs.map
